// Workspace identity — moved verbatim from the app path that used to hold it.
// The re-export shim left behind there reached zero importers and has been
// deleted, so this module is the only definition site.
// R1 CURATED (ARCFE-985): the raw WRITABLE atom no longer reaches the `/data`
// door — reads go through `useWorkspaceId` (React) or `workspaceIdReadAtom`
// (derivations) or `getWorkspaceId` (non-React); writes through the
// `setWorkspaceId` command. `getWorkspaceId` / `setWorkspaceId` /
// `getWorkspaceIdHeader` are the EXPLICIT non-React escape hatch — they exist
// because `scribeHTTP.ts` stamps the header at request time and the SSE
// source does the same, both outside React and synchronous on the hot path.
// Test arrangement on custom stores imports the writable atom through the
// `@heidi/workspace/testing` door, never `/data`.
//
// §9.2 / G2: the storage key, the storage adapter and the `getOnInit: true`
// hydration semantics below are byte-preserved from the pre-move module. A
// change to any of them orphans every signed-in user's persisted workspace and
// needs a migration in the same PR — see `workspaceAtoms.test.ts`, which pins
// the key literal and proves a pre-move value hydrates through this module.
import {
  createSafeJotaiLocalStorage,
  createSafeJotaiSessionStorage,
} from '@heidi/utils/jotai-storage';
import { atom, getDefaultStore } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

export const WORKSPACE_ID_STORAGE_KEY = 'heidi-workspace-id';

/** sessionStorage key of the payload a workspace switch leaves for the document it reloads into. */
export const WORKSPACE_SWITCH_STORAGE_KEY = 'heidi-workspace-switch';

/** The workspace the user switched to: what the loader shows while the reloaded document boots into it. */
export interface WorkspaceSwitchPayload {
  workspaceId: string;
  workspaceName: string;
}

/**
 * Persists the active workspace ID to localStorage so it survives page
 * refreshes and is available immediately on hydration.
 *
 * Cleared on logout via {@link clearLogoutStorage}.
 */
export const workspaceIdAtom = atomWithStorage<string | null>(
  WORKSPACE_ID_STORAGE_KEY,
  null,
  createSafeJotaiLocalStorage<string | null>(),
  { getOnInit: true }
);

/**
 * Get the current workspace ID from the Jotai store.
 * Safe to call outside React (e.g. axios interceptors).
 */
export const getWorkspaceId = (): string | null => {
  return getDefaultStore().get(workspaceIdAtom);
};

/**
 * Set the active workspace ID on the Jotai store. Safe to call outside React
 * (e.g. from an async flow) when a component-scoped `useSetAtom` isn't
 * available — {@link useHydrateWorkspaceId} remains the boot-time owner.
 */
export const setWorkspaceId = (workspaceId: string | null): void => {
  getDefaultStore().set(workspaceIdAtom, workspaceId);
};

/**
 * The workspace the user just switched to, left for the document the switch
 * reloads into; `null` on every other boot. sessionStorage-backed so it crosses
 * the reload but stays in this tab. The switch loader reads it once on mount
 * and resets it (which removes the key), so the next boot in the tab is plain.
 */
export const workspaceSwitchAtom = atomWithStorage<WorkspaceSwitchPayload | null>(
  WORKSPACE_SWITCH_STORAGE_KEY,
  null,
  createSafeJotaiSessionStorage<WorkspaceSwitchPayload | null>(),
  { getOnInit: true }
);

/**
 * Switches to `target` for the document the caller is about to reload into:
 * activates the id and leaves the payload the switch loader shows while that
 * document boots. The caller navigates.
 */
export const switchWorkspace = (target: WorkspaceSwitchPayload): void => {
  getDefaultStore().set(workspaceSwitchAtom, target);
  setWorkspaceId(target.workspaceId);
};

export const HEIDI_WORKSPACE_ID_HEADER = 'Heidi-Workspace-Id';

/**
 * The header's WebSocket twin. A browser cannot set a header on an upgrade, so
 * a socket carries the workspace as `?workspace_id=`; the two spellings are one
 * backend contract and belong together, so a rename lands in one place.
 */
export const WORKSPACE_ID_QUERY_PARAM = 'workspace_id';

/**
 * Returns the workspace-id request header when a workspace is active, otherwise
 * an empty object. Spread into header literals so SSE callers tag spans with
 * `user.workspace_id` the same way the axios interceptors do.
 */
export const getWorkspaceIdHeader = (): Record<string, string> => {
  const workspaceId = getWorkspaceId();
  return workspaceId ? { [HEIDI_WORKSPACE_ID_HEADER]: workspaceId } : {};
};

/**
 * Read-only view of the workspace id for atom DERIVATIONS (law-6 permits
 * read-only exports). Components use `useWorkspaceId()`; non-React code uses
 * `getWorkspaceId()`.
 */
export const workspaceIdReadAtom = atom((get) => get(workspaceIdAtom));
